import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'lodash';
import moment from 'moment';

import { LocaleService } from '@sm360/widget-commons-core-services';
import {
    FIELD_LENGTH,
    Groups,
    InputText,
    ErrorsHandler,
    GroupResponseService,
    ValidationService,
} from '@sm360/widget-commons-forms';

import { GENDERS } from 'financing/constants/profile.js';

import PersonalInfoDisplay from 'financing/components/ApplicantInfosForm/PersonalInfoDisplay.jsx';
import ButtonBlock from 'financing/components/Utils/ButtonBlock.jsx';

import ErrorService from 'financing/services/ErrorService/ErrorService.js';

class ApplicantInfosForm extends React.Component {
    constructor(props) {
        super(props);
        const { personalInformations } = this.props;

        this.state = {
            isSubmitted: false,
            isValid: false,
            inputs: {
                ...personalInformations,
                dayOfBirth: (personalInformations.dateOfBirth && personalInformations.dateOfBirth.getDay()) || '',
                monthOfBirth: (personalInformations.dateOfBirth && personalInformations.dateOfBirth.getMonth()) || '',
                yearOfBirth: (personalInformations.dateOfBirth && personalInformations.dateOfBirth.getFullYear()) || '',
            },
            formValidation: {
                identity: false,
                gender: !!personalInformations.gender,
                dateOfBirth: false,
            },
            dateOfBirthValidation: {
                day: false,
                month: false,
                year: false,
            },
            errors: [],
        };

        this.getPersonalInformationsErrors = this.initializeErrors.bind(this);
        this.onBack = this.onBack.bind(this);
        this.onDateOfBirthChanged = this.onDateOfBirthChanged.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onGenderChange = this.onGenderChange.bind(this);
        this.onIdentityChange = this.onIdentityChange.bind(this);
        this.setEditModePersonalInfoForm = this.setEditModePersonalInfoForm.bind(this);
        this.updateDateOfBirth = this.updateDateOfBirth.bind(this);
        this.updateValidation = this.updateValidation.bind(this);
    }

    componentWillMount() {
        this.updateValidation();
    }

    onDone() {
        const { formValidation } = this.state;

        const errors = ErrorService.getErrors(formValidation, 'FINANCING.APPLICANT_INFO.ERRORS');

        this.setState({
            errors,
            isSubmitted: true,
            isValid: !errors.length,
        });

        this.props.onDone(
            GroupResponseService.createResponse(!errors.length, this.state.inputs, null, null, 'personalInformations')
        );
    }

    onBack(callbackObject) {
        this.props.onBack(
            GroupResponseService.createResponse(true, callbackObject, null, null, 'employmentInformations')
        );
    }

    onGenderChange(e) {
        const newGender = parseInt(e.target.value, 10);

        this.setState((oldState) => {
            const { inputs, formValidation } = oldState;
            inputs.gender = newGender;
            formValidation.gender = true;

            return {
                inputs,
                formValidation,
            };
        });
    }

    onIdentityChange(result) {
        this.setState((oldState) => {
            const { formValidation, inputs } = oldState;
            formValidation.identity = !result.error;

            const updatedInputs = {
                ...inputs,
                ...result.value,
            };

            return {
                formValidation,
                inputs: updatedInputs,
            };
        });
    }

    onDateOfBirthChanged(newValue, inputName) {
        const value = parseInt(newValue, 10);

        this.setState(
            (oldState) => {
                const { dateOfBirthValidation } = oldState;
                switch (inputName) {
                    case 'Day':
                        dateOfBirthValidation[inputName.toLowerCase()] = value && value <= 31 && value >= 1;
                        break;
                    case 'Month':
                        dateOfBirthValidation[inputName.toLowerCase()] = value && value <= 12 && value >= 1;
                        break;
                    case 'Year':
                        dateOfBirthValidation[inputName.toLowerCase()] = value && value <= new Date().getFullYear();
                        break;
                    default:
                        break;
                }

                return { dateOfBirthValidation };
            },
            () => {
                this.updateDateOfBirth(value, inputName);
            }
        );
    }

    setEditModePersonalInfoForm(value) {
        this.setState({ editModePersonalInfoForm: value });
    }

    updateValidation() {
        const isIdentityInformationValid =
            !isEmpty(this.state.inputs.firstname) &&
            !isEmpty(this.state.inputs.lastname) &&
            ValidationService.validatePhoneNumber(this.state.inputs.phoneNumber) &&
            ValidationService.validateEmail(this.state.inputs.email);

        this.setState((oldState) => ({
            editModePersonalInfoForm: !isIdentityInformationValid,
            formValidation: {
                ...oldState.formValidation,
                identity: isIdentityInformationValid,
            },
        }));
    }

    initializeErrors() {
        const { personalInformations } = this.props;
        const { gender, dayOfBirth, monthOfBirth, yearOfBirth } = personalInformations;
        const errors = [];

        if (!gender || gender === '') {
            errors.push({
                code: 'error.gender',
                message: LocaleService.t('FINANCING.APPLICANT_INFO.ERRORS.GENDER'),
            });
        }

        if (!(dayOfBirth && monthOfBirth && yearOfBirth)) {
            errors.push({
                code: 'error.group.dateOfBirth',
                message: LocaleService.t('FINANCING.APPLICANT_INFO.ERRORS.DATE_OF_BIRTH'),
            });
        }

        return errors;
    }

    updateDateOfBirth(newValue, inputName) {
        let { dayOfBirth, monthOfBirth, yearOfBirth } = this.state.inputs;

        switch (inputName) {
            case 'Day':
                dayOfBirth = newValue;
                break;
            case 'Month':
                monthOfBirth = newValue;
                break;
            case 'Year':
                yearOfBirth = newValue;
                break;
            default:
                break;
        }

        const dateOfBirthIsFull = dayOfBirth && monthOfBirth && yearOfBirth;

        this.setState((oldState) => {
            const { inputs, formValidation, dateOfBirthValidation } = oldState;

            inputs.dayOfBirth = dayOfBirth;
            inputs.monthOfBirth = monthOfBirth;
            inputs.yearOfBirth = yearOfBirth;

            formValidation.dateOfBirth = false;

            if (
                dateOfBirthIsFull &&
                dateOfBirthValidation.year &&
                dateOfBirthValidation.month &&
                dateOfBirthValidation.day
            ) {
                const dateOfBirth = moment([yearOfBirth, monthOfBirth - 1, dayOfBirth]);
                formValidation.dateOfBirth = dateOfBirth.isValid();
                if (formValidation.dateOfBirth) {
                    inputs.dateOfBirth = dateOfBirth.toDate();
                } else {
                    inputs.dateOfBirth = null;
                }
            }
            return { inputs, formValidation };
        });
    }

    render() {
        const { editModePersonalInfoForm, inputs, dateOfBirthValidation, errors, isSubmitted, isValid } = this.state;
        const { gender, dayOfBirth, monthOfBirth, yearOfBirth } = inputs;
        const { isOpen, isCoApplicant } = this.props;
        const required = true;

        const personalInfoDisplay = (
            <div>
                <PersonalInfoDisplay informations={inputs} multiLine={required} />
                <button className="sr-link" onClick={() => this.setEditModePersonalInfoForm(true)}>
                    {this.props.isCoApplicant
                        ? LocaleService.t('FINANCING.APPLICANT_INFO.EDIT_COAPPLICANT')
                        : LocaleService.t('FINANCING.APPLICANT_INFO.EDIT_PRIMARY_APPLICANT')}{' '}
                    &gt;
                </button>
            </div>
        );

        const identityLabels = {
            firstname: LocaleService.t('FINANCING.APPLICANT_INFO.FIRST_NAME'),
            lastname: LocaleService.t('FINANCING.APPLICANT_INFO.LAST_NAME'),
            phoneNumber: LocaleService.t('FINANCING.APPLICANT_INFO.PHONE_NUMBER'),
            email: LocaleService.t('FINANCING.APPLICANT_INFO.EMAIL'),
        };

        const personalInfoForm = (
            <div>
                <Groups.IdentityGroup
                    values={inputs}
                    labels={identityLabels}
                    onChange={this.onIdentityChange}
                    hasBeenSubmit={isSubmitted}
                    isRestrictUpdateInfo
                />
            </div>
        );

        const applicantForm = (
            <div className="clearfix">
                {!editModePersonalInfoForm ? personalInfoDisplay : personalInfoForm}
                <div className="financing-form-inline-gender clearfix">
                    <h4 className="sr-text is-bold">{LocaleService.t('FINANCING.APPLICANT_INFO.GENDER')}</h4>
                    <div className="radio-button-cta-wrapper">
                        <div className="radio-button-cta-wrapper--single">
                            <input
                                type="radio"
                                id="maleOption"
                                name="genderOption"
                                value={GENDERS.MALE}
                                checked={gender === GENDERS.MALE}
                                onChange={this.onGenderChange}
                            />
                            <label htmlFor="maleOption">{LocaleService.t('FINANCING.APPLICANT_INFO.MALE')}</label>
                        </div>
                        <div className="radio-button-cta-wrapper--single">
                            <input
                                type="radio"
                                id="femaleOption"
                                name="genderOption"
                                value={GENDERS.FEMALE}
                                checked={gender === GENDERS.FEMALE}
                                onChange={this.onGenderChange}
                            />
                            <label htmlFor="femaleOption">{LocaleService.t('FINANCING.APPLICANT_INFO.FEMALE')}</label>
                        </div>
                    </div>
                </div>
                <div className="financing-form-inline-birth clearfix">
                    <h4 className="sr-text is-bold">{LocaleService.t('FINANCING.APPLICANT_INFO.DATE_OF_BIRTH')}</h4>
                    <InputText
                        label={LocaleService.t('FINANCING.APPLICANT_INFO.YEAR')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.USER_YEAR')}
                        name="Year"
                        type="number"
                        value={yearOfBirth}
                        classNames={['styled-placeholder quarter']}
                        isValid={dateOfBirthValidation.year}
                        isRequired={required}
                        valueChange={this.onDateOfBirthChanged}
                        hasBeenSubmit={isSubmitted}
                        maxLength={FIELD_LENGTH.YEAR_MAX}
                    />
                    <InputText
                        label={LocaleService.t('FINANCING.APPLICANT_INFO.MONTH')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.USER_MONTH')}
                        name="Month"
                        type="number"
                        value={monthOfBirth}
                        classNames={['styled-placeholder quarter']}
                        isValid={dateOfBirthValidation.month}
                        isRequired={required}
                        valueChange={this.onDateOfBirthChanged}
                        hasBeenSubmit={isSubmitted}
                        maxLength={FIELD_LENGTH.MONTH_MAX}
                    />
                    <InputText
                        label={LocaleService.t('FINANCING.APPLICANT_INFO.DAY')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.USER_DAY')}
                        name="Day"
                        type="number"
                        value={dayOfBirth}
                        classNames={['styled-placeholder quarter']}
                        isValid={dateOfBirthValidation.day}
                        isRequired={required}
                        valueChange={this.onDateOfBirthChanged}
                        hasBeenSubmit={isSubmitted}
                        maxLength={FIELD_LENGTH.DAY_MAX}
                    />
                </div>
                {errors.length !== 0 &&
                    isSubmitted &&
                    isOpen && (
                        <p className="financing__content-errors-message">
                            {LocaleService.t('COMMONS_FORMS.ERRORS.TITLE')}
                        </p>
                    )}
                <ButtonBlock onBack={isCoApplicant ? this.onBack : undefined} onDone={this.onDone} />
            </div>
        );
        const formClass = isOpen
            ? 'financingBlockForm applicantForm is-open'
            : `financingBlockForm applicantForm is-closed ${isValid ? 'is-valid' : ''}`;

        return (
            <div className={formClass}>
                <icon className="icon-Crochet is-util" />
                {errors.length !== 0 && isSubmitted && isOpen && <ErrorsHandler errors={errors} />}
                <h3 className="sr-text is-bold is-util">
                    {this.props.isCoApplicant
                        ? LocaleService.t('FINANCING.APPLICANT_INFO.WHO_IS_COAPPLICANT')
                        : LocaleService.t('FINANCING.APPLICANT_INFO.WHO_IS_PRIMARY_APPLICANT')}
                </h3>
                <br />
                {isOpen ? applicantForm : <PersonalInfoDisplay informations={inputs} />}
            </div>
        );
    }
}

ApplicantInfosForm.defaultProps = {
    isCoApplicant: false,
    isOpen: true,
    onBack: noop,
};

ApplicantInfosForm.propTypes = {
    isCoApplicant: PropTypes.bool,
    isOpen: PropTypes.bool,
    onBack: PropTypes.func,
    onDone: PropTypes.func.isRequired,
    personalInformations: PropTypes.shape({
        dateOfBirth: PropTypes.instanceOf(Date),
        dayOfBirth: PropTypes.instanceOf(Date),
        monthOfBirth: PropTypes.instanceOf(Date),
        yearOfBirth: PropTypes.instanceOf(Date),
        email: PropTypes.string.isRequired,
        firstname: PropTypes.string.isRequired,
        gender: PropTypes.string.isRequired,
        lastname: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string.isRequired,
    }).isRequired,
};

ApplicantInfosForm.displayName = 'ApplicantInfosForm';

export default ApplicantInfosForm;
